/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { getTopicId, isTopicFavorite } from '@e180/domain/lib/topic/util';

import styles from './TopicFavoriteButton.style';

import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import StarIcon from 'src/shared/ui/component/icon/StarIcon';

import type { Topic } from '@e180/domain/lib/topic/type';

type Props = {
  topic: Topic,
  large?: boolean,
  lightTheme?: boolean,
  handleClick: (isBookmarked: boolean, topicId: number) => void,
};

const useStyles = createUseStyles(styles);

const TopicFavoriteButton = ({
  handleClick,
  topic,
  large,
  lightTheme,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor, darkAccentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isBookmarked, setIsBookmarked] = React.useState(
    isTopicFavorite(topic),
  );

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onClick = (e: MouseEvent) => {
    // Required, otherwise navigate to topic
    e.preventDefault();

    const value = !isBookmarked;

    handleClick(value, getTopicId(topic));

    // We implement an optimistic action, therefore the state is updated
    // before we know the API call succeeded
    setIsBookmarked(value);
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({
    lightTheme,
    darkAccentColor,
    accentColor,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div>
      <div className={classes.star} onClick={onClick}>
        <StarIcon
          className={classes.favorite}
          isFilled={isBookmarked}
          large={large}
        />
      </div>
    </div>
  );
};

export default compose(React.memo)(TopicFavoriteButton);
